import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ImageSize } from '../../cms/import/main-preview/main-preview.component';
import { ImportItem } from '../../cms/import/import.component';
import { environment } from 'src/app/shared/environment';
import { HttpClient } from '@angular/common/http';
import { GalleriesService } from '../../cms/content/galleries/galleries.service';
import { ProductsService } from '../../cms/content/products/products.service';
import { LoadingService } from '../loading.service';
import { SnackBarService } from '../../share/snack-bar.service';

@Component({
  selector: 'app-main-preview-item',
  templateUrl: './main-preview-item.component.html',
  styleUrls: ['./main-preview-item.component.scss'],
})
export class MainPreviewItemComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() importItem?: ImportItem;
  @Input() isVideo: boolean = false;
  @Input() loadingPreviewUrl?: string;
  @Input() previewUrl?: string;
  @Input() largePreviewUrl?: string;
  @Input() isLocked?: boolean;
  @Input() lockedBy?: string;
  @Input() isError?: boolean;
  @Input() videoPreviewUrl?: string;
  @Input() isAiProcessing?: boolean = false;
  @Input() aiStatus?: string;
  @Input() showFaceAI?: boolean = false; // FaceAI switch control

  isPreviewImageLoaded: boolean = false;

  @ViewChild('videoPlayer') videoPlayer?: ElementRef<HTMLVideoElement>;
  @ViewChild('mainImage') mainImage?: ElementRef<HTMLImageElement>;
  @ViewChild('imageWrapper') imageWrapper?: ElementRef<HTMLDivElement>;
  @ViewChild('imageInnerWrapper') imageInnerWrapper?: ElementRef<HTMLDivElement>;

  @Input() canDownload: boolean = false;
  @Input() canSetAsGalleryHead: boolean = false;

  @Input() imageSize: ImageSize = ImageSize.Normal;

  private resizeObserver?: ResizeObserver;

  @Output() onImageLoaded: EventEmitter<{
    item: ImportItem;
    aspectRatio: number;
  }> = new EventEmitter<{ item: ImportItem; aspectRatio: number }>();
  @Output() onPreviewImageLoaded: EventEmitter<{
    item: ImportItem;
    aspectRatio: number;
  }> = new EventEmitter<{ item: ImportItem; aspectRatio: number }>();

  imagePreviewUrl?: string;
  imageLoadingPreviewUrl?: string;

  imageLoaded: boolean = false;
  isImageError: boolean = false;

  shouldLoadBlur: boolean = false;

  constructor(
    private http: HttpClient,
    private productsService: ProductsService,
    private galleriesService: GalleriesService
  ) {}

  ngOnInit(): void {
    // Lifecycle hook - can be used for initialization logic if needed
  }

  ngAfterViewInit(): void {
    this.setupResizeObserver();
  }

  setupResizeObserver() {
    if (!this.imageWrapper) return;

    this.resizeObserver = new ResizeObserver(() => {
      if (this.imageLoaded) {
        this.calculateImageScale();
      }
    });

    this.resizeObserver.observe(this.imageWrapper.nativeElement);
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    this.videoPreviewUrl = this.getVideoPreviewUrl();
    this.imagePreviewUrl = this.getPreviewUrl();
    this.imageLoadingPreviewUrl = this.getLoadingUrl();

    if (this.imageLoadingPreviewUrl.includes('https://')) {
      this.shouldLoadBlur = false;
    }
  }

  /**
   * Provjerava je li status u procesu obrade (sadrži "...")
   */
  isProcessingStatus(status: string | boolean | null | undefined): boolean {
    // Provjeri je li status uopće string
    if (typeof status !== 'string') {
      return false;
    }
    return status.includes('...');
  }

  /**
   * Provjerava je li status označava uspješno završeni proces
   */
  isCompleteStatus(status: string | boolean | null | undefined): boolean {
    // Provjeri je li status uopće string
    if (typeof status !== 'string') {
      return false;
    }
    return (status.toLowerCase().includes('complete') ||
        status.toLowerCase().includes('done')) &&
      !this.isProcessingStatus(status);
  }

  /**
   * Provjerava je li status označava neuspjeli proces
   */
  isFailedStatus(status: string | boolean | null | undefined): boolean {
    // Provjeri je li status uopće string
    if (typeof status !== 'string') {
      return false;
    }
    return status.toLowerCase().includes('failed') &&
      !this.isProcessingStatus(status);
  }


  buildUrl(url: string | undefined): string {
    if (url == undefined) return '';

    //if the url starts with assets then it is a local img and it should just return the url
    if (this.previewUrl?.split('/')[1] === 'assets') return this.previewUrl;

    if (url.includes('https://') || url.includes('http://')) {
      return url;
    }

    return environment.productServingUrl + url;
  }

  getPreviewUrl(): string {
    switch (this.imageSize) {
      case ImageSize.Normal:
        return this.buildUrl(this.previewUrl);
      case ImageSize.Large:
        return this.buildUrl(this.largePreviewUrl);
    }
  }

  getDownloadUrl(): string {
    return this.buildUrl(this.largePreviewUrl);
  }

  getLoadingUrl(): string {
    return this.buildUrl(this.loadingPreviewUrl);
  }

  loadedPreviewImage($event: any) {
    this.isPreviewImageLoaded = true;
    let target: any = $event.target;
    let aspectRatio: number = target.naturalWidth / target.naturalHeight;

    this.onPreviewImageLoaded.emit({
      item: this.importItem!,
      aspectRatio: aspectRatio,
    });
  }

  loadedVideoPreviewImage($event: any) {
    let target: any = $event.target;
    let aspectRatio: number = target.naturalWidth / target.naturalHeight;

    this.onPreviewImageLoaded.emit({
      item: this.importItem!,
      aspectRatio: aspectRatio,
    });
  }

  loadedImage($event: any) {
    this.imageLoaded = true;

    let target: any = $event.target;
    let aspectRatio: number = target.naturalWidth / target.naturalHeight;    

    this.onImageLoaded.emit({
      item: this.importItem!,
      aspectRatio: aspectRatio,
    });

    // Calculate scale for object-fit: cover behavior
    this.calculateImageScale();
  }

  calculateImageScale() {
    if (!this.mainImage || !this.imageWrapper) return;

    const img = this.mainImage.nativeElement;
    const container = this.imageWrapper.nativeElement;
    const innerWrapper = this.imageInnerWrapper?.nativeElement;

    if (!innerWrapper) return;

    // Wait for image to have natural dimensions
    if (img.naturalWidth === 0 || img.naturalHeight === 0) {
      setTimeout(() => this.calculateImageScale(), 10);
      return;
    }

    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;
    const imgWidth = img.naturalWidth;
    const imgHeight = img.naturalHeight;

    // Calculate scale to cover container (like object-fit: cover)
    const scaleX = containerWidth / imgWidth;
    const scaleY = containerHeight / imgHeight;
    const scale = Math.max(scaleX, scaleY);

    // Apply scale
    const scaledWidth = imgWidth * scale;
    const scaledHeight = imgHeight * scale;

    // Set dimensions on inner wrapper
    innerWrapper.style.width = `${scaledWidth}px`;
    innerWrapper.style.height = `${scaledHeight}px`;

    // Center horizontally, align top (object-position: 50% top)
    const offsetX = (containerWidth - scaledWidth) / 2;
    innerWrapper.style.left = `${offsetX}px`;
    innerWrapper.style.top = '0';
  }

  loadedVideo($event: any) {
    this.videoPlayer?.nativeElement.pause();
  }

  erroredImage($event: any) {
    this.isImageError = true;
  }

  getVideoPreviewUrl(): string {
    if (!this.videoPreviewUrl) return '';

    return environment.productServingUrl + `${this.videoPreviewUrl}`;
  }

  setAsGalleryHead($event: MouseEvent) {
    $event.preventDefault();

    this.galleriesService.setAsGalleryHead(this.importItem!);
  }

  videoOver() {
    this.videoPlayer?.nativeElement.play();
  }

  videoOut() {
    this.videoPlayer?.nativeElement.pause();
  }

  /*
   * DOWNLOADING
   */

  download($event: MouseEvent) {
    $event.preventDefault();
    this.productsService.downloadFileForCms(
      'PRODUCT.jpeg',
      this.importItem!.id
    );
  }
}
